import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

export function LegalLayout({
  eyebrow,
  title,
  updated,
  intro,
  children,
}: {
  eyebrow: string;
  title: string;
  updated: string;
  intro: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl px-5 py-section lg:px-8">
      <p className="eyebrow text-primary">{eyebrow}</p>
      <h1 className="mt-2 font-display text-h1 md:text-display">{title}</h1>
      <p className="mt-3 text-small text-ink-muted">Senast uppdaterad {updated}.</p>
      <p className="mt-6 text-h4 text-ink-muted">{intro}</p>

      <nav aria-label="Övriga juridiska sidor" className="mt-8 flex flex-wrap gap-2">
        <LegalChip to="/integritet" label="Integritetspolicy" />
        <LegalChip to="/cookies" label="Cookies" />
        <LegalChip to="/villkor" label="Användarvillkor" />
        <LegalChip to="/villkor/skolor" label="Regler för skolor" />
        <LegalChip to="/tillganglighet" label="Tillgänglighet" />
      </nav>

      <div className="prose-legal mt-10 space-y-8 text-ink">{children}</div>

      <p className="mt-16 rounded-card border border-border bg-surface p-card text-small text-ink-muted">
        Har du frågor? Kontakta oss på{" "}
        <a href="mailto:hej@skolvyn.se" className="font-medium text-primary underline underline-offset-2">hej@skolvyn.se</a>{" "}
        eller läs mer{" "}
        <Link to="/om" className="font-medium text-primary underline underline-offset-2">om Skolvyn</Link>.
      </p>
    </div>
  );
}

function LegalChip({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="rounded-pill border border-border bg-surface px-3.5 py-1.5 text-small font-medium text-ink-muted hover:bg-primary-soft hover:text-primary"
      activeProps={{ className: "border-primary bg-primary-soft text-primary" }}
    >
      {label}
    </Link>
  );
}

export function LegalSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="scroll-mt-24">
      <h2 id={id} className="font-display text-h2">{title}</h2>
      <div className="mt-3 space-y-3 text-ink-muted [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_li]:ml-5 [&_li]:list-disc [&_p]:leading-relaxed">
        {children}
      </div>
    </section>
  );
}
